import { Fragment, useEffect, useState } from 'react'
import { Link, useHistory } from 'react-router-dom'
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd'
import { useSelector } from 'statux'

import Badge from '../../../../components/Badge/Badge'
import Table from '../../../../components/Table/Table'

import { graphqlClient, gql } from '../../../../utils/gql'
import authProvider from '../../../../utils/auth-provider'

export default function DashboardEbpcAssignmentsTable (props = {
    draggable: false,
    ventureColText: null,
    rankingColText: null,
    rankingStatusBadge: false,
    rankingBtnLink: false,
    refreshAssignments: false
}) {
    const role = authProvider.getDefaultRole()

    const isEbpcJudge = role === 'ebpc_judge'

    const history = useHistory()

    const [assignments, setAssignments] = useState(
        (history.state || {}).ebpcAssignments
            || null)

    const {
        ebpcContest,
        ebpcFinalsOpen
    } = useSelector()

    const [draggable, setDraggable] = useState(props.draggable)

    const onBeforeDragStart = (e) => {
        const row = document.querySelector(`#${e.draggableId}`) || {}

        Array.from(row.children || [])
            .concat(row
                ? Array.from(row.parentElement.previousElementSibling
                    .querySelectorAll('td'))
                : [])
            .forEach(el => (el.style.width = `${el.clientWidth}px`))
    }

    const onDragEnd = async (e) => {
        const row = document.querySelector(`#${e.draggableId}`) || {}

        Array.from(row.children || [])
            .concat(row
                ? Array.from(row.parentElement.previousElementSibling
                    .querySelectorAll('td'))
                : [])
            .forEach(el => (el.style.width = ''))

        if (e.source.index === e.destination.index)
            return

        assignments.splice(
            e.destination.index,
            0,
            assignments.splice(e.source.index, 1)[0])

        setAssignments(assignments)

        const mutations = assignments
            .map((obj, idx) => gql`
                update_ranking_${idx}: update_ebpc_assignments_by_pk (
                    pk_columns: {
                        ebpc_judge_id: "${obj.ebpc_judge_id}",
                        ebpc_application_id: ${obj.ebpc_application_id},
                        ebpc_finals: ${ebpcFinalsOpen}
                    },
                    _set: { ranking: ${idx + 1} }
                ) {
                    ranking
                }
            `)
            .join('\n')

        await graphqlClient
            .request(gql`
                mutation update_ebpc_assignments_rankings {
                    ${mutations}
                }
            `)
            .catch(console.warn)
    }

    useEffect(async () => {
        if (!isEbpcJudge
                || !(ebpcContest || {}).id)
            return

        const query = await graphqlClient
            .request(gql`
                query {
                    ebpc_assignments (
                        where: {
                            recused_at: {
                                _is_null: true
                            },
                            ebpc_judge_id: {
                                _eq: "${authProvider.getIdentity().id}"
                            },
                            ebpc_application: {
                                ebpc_contest_id: {
                                    _eq: ${ebpcContest.id}
                                },
                                submitted_at: {
                                    _is_null: false
                                }
                            },
                            ebpc_finals: {
                                _eq: ${ebpcFinalsOpen}
                            }
                        },
                        order_by: [
                            { ranking: asc },
                            { rating: desc_nulls_last },
                            { ebpc_application_id: asc }
                        ]
                    ) {
                        invest
                        ranking
                        rating
                        submitted_at
                        ebpc_judge_id
                        ebpc_application_id
                        ebpc_application {
                            id
                            data
                            venture: data(path: "aboutYourVenture.ventureName")
                            profile {
                                company
                                user {
                                    id
                                }
                            }
                        }
                        ebpc_ratings (where: {
                            ${ebpcFinalsOpen
                                ? gql`ebpc_finals: { _eq: true }`
                                : ''}
                        }) {
                            type
                            rating
                        }
                    }

                    ebpc_ratings_types_aggregate {
                        aggregate {
                            count
                        }
                    }
                }
            `)
            .then(obj => obj.ebpc_assignments
                .map(_obj => {
                    const ratingsTypesCount = obj.ebpc_ratings_types_aggregate
                        .aggregate
                        .count || 0

                    _obj.status = 'Not started'
                    _obj.statusType = null

                    if (_obj.ebpc_ratings.length
                            || _obj.rating
                            || _obj.invest !== null) {
                        _obj.status = 'In progress'
                        _obj.statusType = 'warning'
                    }

                    if (ratingsTypesCount === _obj.ebpc_ratings.length
                            && _obj.rating
                            && _obj.invest !== null) {
                        _obj.status = 'Completed'
                        _obj.statusType = 'success'
                    }

                    _obj.plan = _obj.ebpc_application.id

                    _obj.venture = _obj.ebpc_application.venture
                        || _obj.ebpc_application.profile.company

                    _obj.applicant = _obj.ebpc_application.profile.user

                    return _obj
                }))
            .catch((err) => {
                console.warn(err)
                return []
            })

        if (draggable
                && !query.filter(obj => !obj.submitted_at).length)
            setDraggable(false)

        if (JSON.stringify(assignments)
                === JSON.stringify(query))
            return

        setAssignments(query)

        history.replace(
            `${history.location.pathname}${history.location.search}`,
            Object.assign(history.location.state || {}, {
                ebpcAssignments: query
            }))
    }, [props.refreshAssignments])

    useEffect(async () => {
        if (assignments === null)
            return

        history.replace(
            `${history.location.pathname}${history.location.search}`,
            Object.assign(history.location.state || {}, {
                ebpcAssignments: assignments
            }))
    }, [assignments])

    if (!isEbpcJudge)
        return null

    if (!(assignments || []).length)
        return (
            <p className="body-4">
                You have not been assigned any applications yet,
                as the competition is ongoing and applications are still
                being submitted. You will receive an email notification
                when applications have been assigned for your review.
            </p>
        )

    const renderTableRow = (obj, idx, rowProps = {}) => (
        <tr
            id={rowProps['data-rbd-draggable-id']}
            data-dragging={((rowProps.style || {}).position === 'fixed'
                && rowProps.style.pointerEvents === 'none')
                    || null}
            {...rowProps}
        >
            {draggable
                && (
                    <td
                        className="table-col-drag-indicator"
                    >
                        <svg
                            className="icon-drag-indicator"
                            aria-hidden="true"
                            width="16"
                            height="16"
                        >
                            <use xlinkHref="#icon-drag-indicator" />
                        </svg>
                        <span>{idx + 1}</span>
                    </td>
                )}
            <td
                className="table-col-venture"
            >
                <Link
                    to={`/dashboard/ebpc/applications/${obj.applicant.id}`}
                    title="View application"
                >
                    <span>{obj.venture}</span>
                </Link>
            </td>
            <td>
                <span
                    className={`rating${!obj.rating
                        ? ' rating-none'
                        : ''}`}
                    style={{
                        marginLeft: 5,
                        marginRight: !props.rankingStatusBadge
                                && props.rankingBtnLink
                            ? 20
                            : 15
                    }}
                >
                    {obj.rating || <span aria-label="Not availabe">-</span>}
                </span>
                {(props.rankingStatusBadge
                        || obj.statusType !== 'success')
                    && (
                        <Badge
                            type={obj.statusType}
                        >
                            {obj.status}
                        </Badge>)}
                {!props.rankingStatusBadge
                    && props.rankingBtnLink
                    && obj.statusType === 'success'
                    && (
                        <Link
                            to={`/dashboard/ebpc/applications/${obj.applicant.id}`}
                            className="btn btn-sm btn-outline-secondary-alt"
                            style={{
                                fontSize: 12
                            }}
                        >
                            Modify rating
                        </Link>
                    )}
            </td>
        </tr>
    )

    return (
        <Table
            className="dashboard-ebpc-assignments-table"
        >
            <thead>
                <tr>
                    {draggable
                        && <th>Rank</th>}
                    <th>{props.ventureColText || 'Name'}</th>
                    <th>{props.rankingColText || 'Your rating'}</th>
                </tr>
            </thead>
            {!draggable
                && (
                    <tbody>
                        {assignments
                            .map((obj, idx) => (
                                <Fragment
                                    key={`dashboard-ebpc-assigments-table-row-${idx}`}
                                >
                                    {renderTableRow(obj, idx)}
                                </Fragment>
                            ))}
                    </tbody>)}
            {draggable
                && (
                    <DragDropContext
                        onDragEnd={onDragEnd}
                        onBeforeDragStart={onBeforeDragStart}
                    >
                        <Droppable droppableId="dashboard-ebpc-assignements-table">
                            {(provided, snapshot) => (
                                <tbody
                                    ref={provided.innerRef}
                                    style={{
                                        userSelect: snapshot.isDraggingOver
                                            ? 'none'
                                            : 'auto'
                                    }}
                                >
                                    {assignments
                                        .map((obj, idx) => (
                                            <Draggable
                                                key={`dashboard-ebpc-assigments-table-row-${idx}`}
                                                draggableId={`dashboard-ebpc-assigments-table-row-${idx}`}
                                                index={idx}
                                            >
                                                {(providedDraggable, snapshotDraggable) => (
                                                    renderTableRow(obj, idx, {
                                                        ref: providedDraggable.innerRef,
                                                        ...providedDraggable.draggableProps,
                                                        ...providedDraggable.dragHandleProps,
                                                        draggable: true,
                                                        style: {
                                                            userSelect: snapshotDraggable.isDraggingOver
                                                                ? 'none'
                                                                : 'auto',
                                                            ...providedDraggable.draggableProps.style
                                                        }
                                                    })
                                                )}
                                            </Draggable>
                                        ))}
                                    {provided.placeholder}
                                </tbody>
                            )}
                        </Droppable>
                    </DragDropContext>
                )}
        </Table>
    )
}
